/**
 * Legal pages — Privacy Policy & Terms of Service.
 *
 * Owner can edit copy here later (or move it to the CMS). Footer links target
 * /privacy and /terms which are routed to <Privacy /> and <Terms />.
 */
import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

function Page({ title, lastUpdated, children, testid }) {
  return (
    <div className="bg-[#0B0B0B] text-white min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid={testid}>
        <Link to="/" className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/55 hover:text-[#5FBDD6]">
          ← Back to home
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-4">{title}</h1>
        <p className="text-xs uppercase tracking-[0.18em] text-white/45 mt-2">Last updated: {lastUpdated}</p>
        <div className="prose prose-invert mt-10 max-w-none text-sm leading-relaxed text-white/80 space-y-5">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy" lastUpdated="February 2026" testid="privacy-page">
      <p className="text-white/55 italic">
        This is a placeholder Privacy Policy. The owner will replace this copy with the finalized
        policy from their attorney.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">1. Information we collect</h2>
      <p>
        We collect information you provide directly when you place a pickup order, contact us, request a catering
        quote, or sign up for our email list. This typically includes your name, email address, phone number, and
        order details. We also collect basic analytics (page views, device type) to help us improve the site.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">2. How we use your information</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>To process your pickup orders and contact you about order status.</li>
        <li>To respond to catering and general inquiries.</li>
        <li>To send you marketing emails about menu updates and promotions, only if you opt in.</li>
        <li>To improve our website and service.</li>
      </ul>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">3. Payment information</h2>
      <p>
        Payments are processed by Stripe. We never receive or store your full card number — that information is
        handled entirely by Stripe under their own privacy policy.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">4. Email marketing</h2>
      <p>
        Joining our email list is opt-in. You can unsubscribe at any time using the link in any marketing email
        we send, or by contacting us at <a href="mailto:info@btwnyc.com" className="text-[#5FBDD6] hover:underline">info@btwnyc.com</a>.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">5. Sharing</h2>
      <p>
        We do not sell your personal information. We share information only with vendors that help us run our
        business (e.g., Stripe for payments, Resend for email delivery, our courier partner for deliveries).
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">6. Contact</h2>
      <p>
        Questions about privacy? Email <a href="mailto:info@btwnyc.com" className="text-[#5FBDD6] hover:underline">info@btwnyc.com</a>.
      </p>
    </Page>
  );
}

export function Terms() {
  return (
    <Page title="Terms of Service" lastUpdated="February 2026" testid="terms-page">
      <p className="text-white/55 italic">
        This is a placeholder Terms of Service. The owner will replace this copy with the finalized
        terms from their attorney.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">1. Use of the site</h2>
      <p>
        By using btwnyc.com you agree to these terms. The site and its content are for your personal,
        non-commercial use.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">2. Orders & payments</h2>
      <p>
        All pickup orders are subject to availability. Prices and menu items may change without notice.
        We charge your payment method through Stripe at the time of order. Refunds for prepared food are
        provided at our discretion.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">3. Pickup windows & operating hours</h2>
      <p>
        Pickup windows are limited to our operating hours. We are closed on Mondays. Please pick up your order
        at the time you selected — orders not picked up within 30 minutes of the chosen window may be refunded
        at our discretion.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">4. Allergens</h2>
      <p>
        Our kitchen prepares food in a shared space with wheat, dairy, soy, eggs, and shellfish.
        Customers with severe allergies should call us directly before ordering.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">5. Disclaimer of warranties</h2>
      <p>
        The site is provided "as is." We do not guarantee that the site will be uninterrupted or error-free.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">6. Changes to terms</h2>
      <p>
        We may update these terms at any time. Continued use of the site after changes constitutes acceptance.
      </p>

      <h2 className="font-display text-xl uppercase tracking-tight text-white mt-8">7. Contact</h2>
      <p>
        Questions? Email <a href="mailto:info@btwnyc.com" className="text-[#5FBDD6] hover:underline">info@btwnyc.com</a>.
      </p>
    </Page>
  );
}
